import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { useActiveGameRedirect } from "../logic/useActiveGameRedirect";
import type { DeckSummary, GameRow, User } from "../api/client";
import { AppHeader } from "../components/AppHeader";
import { EmailVerificationBanner } from "../components/EmailVerificationBanner";
import { pixelTheme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { bevelShadow, defaultBtn, primaryBtn } from "../theme/styles";

export function LobbyPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  useActiveGameRedirect();
  const [user, setUser] = useState<User | null>(null);
  const [decks, setDecks] = useState<DeckSummary[]>([]);
  const [active, setActive] = useState<GameRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [me, d, g] = await Promise.all([
        api.getMe(),
        api.listDecks(),
        api.getActiveGame(),
      ]);
      setUser(me.user);
      setDecks(d.decks);
      setActive(g.active);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onResume() {
    if (active) navigate(`/battle/${active.id}`);
  }

  async function onEndActive() {
    if (!active || busy) return;
    setBusy(true);
    try {
      await api.endGame(active.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    try {
      await api.logout();
    } catch {
      // Even on failure, push to login — the session cookie might
      // already be gone.
    }
    // Drop the cached socket token / drop any open WebSocket so the
    // next user starts fresh.
    const { setSocketToken } = await import("../api/rooms");
    setSocketToken(null);
    navigate("/login", { replace: true });
  }

  async function onCreateRoom() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { room_id } = await api.createRoom();
      navigate(`/room/${room_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  function gateForGuest(reason: "packs" | "deck"): boolean {
    if (user?.is_guest) {
      navigate(`/convert?reason=${reason}`);
      return true;
    }
    return false;
  }

  async function onDeleteDeck(deckId: number, deckName: string) {
    if (busy) return;
    if (gateForGuest("deck")) return;
    if (!window.confirm(`Delete "${deckName}"? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteDeck(deckId);
      await load();
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 409) {
        setError("End your active game before deleting this deck.");
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "24px 20px",
      }}
    >
      <div
        style={{
          maxWidth: 880,
          margin: "0 auto",
          display: "grid",
          gap: 20,
        }}
      >
        <AppHeader user={user} onLogout={onLogout} />

        <EmailVerificationBanner user={user} />

        {error && (
          <div
            style={{
              padding: 12,
              background: theme.panel,
              border: `2px solid ${theme.accent}`,
              color: theme.accent,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: theme.inkFaint }}>Loading…</div>
        ) : (
          <>
            {active && (
              <Section theme={theme} title="ACTIVE GAME">
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    padding: 12,
                    background: theme.panel,
                    border: `2px solid ${theme.panelEdge}`,
                    boxShadow: bevelShadow,
                  }}
                >
                  <span style={{ fontSize: 16, flex: "1 1 200px" }}>
                    You have a game in progress.
                  </span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <ActionButton theme={theme} onClick={onResume} kind="primary">
                      RESUME
                    </ActionButton>
                    <ActionButton
                      theme={theme}
                      onClick={onEndActive}
                      disabled={busy}
                    >
                      END GAME
                    </ActionButton>
                  </div>
                </div>
              </Section>
            )}

            <Section theme={theme} title="MULTIPLAYER">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: 12,
                  background: theme.panel,
                  border: `2px solid ${theme.panelEdge}`,
                  boxShadow: bevelShadow,
                }}
              >
                <span style={{ flex: "1 1 200px", color: theme.inkDim }}>
                  Create a room and share the link to play with a friend.
                </span>
                <ActionButton
                  theme={theme}
                  onClick={onCreateRoom}
                  disabled={busy || active !== null}
                  kind="primary"
                  title={active !== null ? "End your active game first" : undefined}
                >
                  CREATE ROOM
                </ActionButton>
              </div>
            </Section>

            <Section theme={theme} title="WORLD">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: 12,
                  background: theme.panel,
                  border: `2px solid ${theme.panelEdge}`,
                  boxShadow: bevelShadow,
                }}
              >
                <span style={{ flex: "1 1 200px", color: theme.inkDim }}>
                  Explore the world of High Fantasia.
                </span>
                <ActionButton
                  theme={theme}
                  onClick={() => navigate("/world")}
                  kind="primary"
                >
                  EXPLORE
                </ActionButton>
              </div>
            </Section>

            <Section theme={theme} title="DECKS">
              {decks.length === 0 ? (
                <div style={{ color: theme.inkFaint }}>
                  No decks yet.
                </div>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                  {decks.map((d) => {
                    const fc = factionFor(d.faction).color;
                    return (
                      <li
                        key={d.id}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          padding: "10px 12px",
                          background: theme.panel,
                          border: `2px solid ${theme.panelEdge}`,
                          borderLeft: `6px solid ${fc}`,
                          boxShadow: bevelShadow,
                        }}
                      >
                        <div style={{ display: "grid", gap: 2, minWidth: 0, flex: "1 1 200px" }}>
                          <div style={{ fontSize: 18, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.name}
                          </div>
                          <div
                            style={{
                              fontFamily: theme.fontDisplay,
                              fontSize: 10,
                              letterSpacing: theme.letterSpacing,
                              color: theme.inkFaint,
                            }}
                          >
                            {d.faction.toUpperCase()}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {/* Starter decks are immutable — no EDIT/DELETE
                              buttons. The lobby just shows them as a
                              fixed loadout the player always has. */}
                          {!d.is_starter && (
                            <>
                              <ActionButton
                                theme={theme}
                                onClick={() => {
                                  if (gateForGuest("deck")) return;
                                  navigate(`/decks/${d.id}/edit`);
                                }}
                                disabled={busy || active !== null}
                                title={
                                  active !== null
                                    ? "End your active game first"
                                    : undefined
                                }
                              >
                                EDIT
                              </ActionButton>
                              <ActionButton
                                theme={theme}
                                onClick={() => onDeleteDeck(d.id, d.name)}
                                disabled={busy || active !== null}
                                title={
                                  active !== null
                                    ? "End your active game first"
                                    : undefined
                                }
                              >
                                DELETE
                              </ActionButton>
                            </>
                          )}
                          {d.is_starter && (
                            <span
                              style={{
                                fontFamily: theme.fontDisplay,
                                fontSize: 9,
                                letterSpacing: theme.letterSpacing,
                                color: theme.inkFaint,
                                alignSelf: "center",
                              }}
                            >
                              Starter Deck
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {active && (
                <div style={{ color: theme.inkFaint, fontSize: 14, marginTop: 8 }}>
                  End your current game before starting a new one.
                </div>
              )}
              {user && <NewDeckRow user={user} decks={decks} onNewDeck={() => {
                if (gateForGuest("deck")) return;
                navigate("/decks/new");
              }} theme={theme} />}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
  theme,
}: {
  title: string;
  children: React.ReactNode;
  theme: typeof pixelTheme;
}) {
  return (
    <section style={{ display: "grid", gap: 8 }}>
      <h2
        style={{
          margin: 0,
          fontFamily: theme.fontDisplay,
          fontSize: 12,
          letterSpacing: theme.letterSpacing,
          color: theme.inkDim,
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function ActionButton({
  theme,
  kind,
  children,
  disabled,
  onClick,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  theme: typeof pixelTheme;
  kind?: "primary" | "default";
}) {
  const inert = !!disabled;
  const props =
    kind === "primary"
      ? primaryBtn(theme, inert)
      : defaultBtn(theme, inert);
  // Use aria-disabled rather than the native `disabled` attribute so a
  // keyboard user's focus survives a busy/in-flight click. The click
  // handler short-circuits while inert.
  return (
    <button
      type="button"
      {...rest}
      aria-disabled={inert || undefined}
      onClick={(e) => {
        if (inert) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

function NewDeckRow({
  user,
  decks,
  onNewDeck,
  theme,
}: {
  user: User;
  decks: DeckSummary[];
  onNewDeck: () => void;
  theme: typeof pixelTheme;
}) {
  // Starter decks (`is_starter`) don't count toward the cap.
  const used = decks.filter((d) => !d.is_starter).length;
  const total = user.deck_slots;
  const slotsFull = used >= total;

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        marginTop: 8,
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        {used}/{total} CUSTOM DECK SLOTS USED
      </div>
      <button
        onClick={onNewDeck}
        disabled={slotsFull}
        title={slotsFull ? "Buy a Deck Box from the Cardmaster to add a slot." : undefined}
        {...primaryBtn(theme, slotsFull)}
      >
        + NEW DECK
      </button>
    </div>
  );
}

